import { useState, useContext } from "react";
import { ThemeContext } from "../appContext";

// ── Constants default values ─────────────────────────────────
export const DEFAULTS = {
  G:                   3.986e14,
  RE:                  6371e3,
  G0:                  9.81,
  SEC_PER_YEAR:        3.156e7,
  CD:                  2.2,
  RHO_A:               4.71e-13,
  RHO_REF_ALT:         500,
  RHO_SCALE:           48,
  ETA_INTRACK:         1.639,
  ETA_OSCILLATORY:     1.172,
  ETA_EP:              1.020,
  W3_FRACTION:         0.10,
  W6_FRACTION:         0.08,
  W4_ANNUAL_PER_100KG: 0.08,
  TIER_SKM4:           0.90,
  TIER_SKM1:           0.95,
  W6_MULT:             0.92,
};

const META = [
  { key:"G",                   unit:"m³/s²",      desc:"Gravitational parameter (μ = GM)",          group:"Orbital" },
  { key:"RE",                  unit:"m",           desc:"Earth equatorial radius",                   group:"Orbital" },
  { key:"G0",                  unit:"m/s²",        desc:"Standard gravity (Isp conversion)",         group:"Orbital" },
  { key:"SEC_PER_YEAR",        unit:"s",           desc:"Seconds per year (365.25 days)",            group:"Orbital" },
  { key:"CD",                  unit:"—",           desc:"Drag coefficient (flat plate tumbling)",    group:"Atmosphere" },
  { key:"RHO_A",               unit:"kg/m³",       desc:"Reference atmospheric density at 500 km",  group:"Atmosphere" },
  { key:"RHO_REF_ALT",         unit:"km",          desc:"Reference altitude for density model",      group:"Atmosphere" },
  { key:"RHO_SCALE",           unit:"km",          desc:"Atmospheric scale height (MSISE-90)",       group:"Atmosphere" },
  { key:"ETA_INTRACK",         unit:"—",           desc:"In-track retrograde overshoot factor",      group:"EP-ADCS η" },
  { key:"ETA_OSCILLATORY",     unit:"—",           desc:"SRP + density variation overshoot",         group:"EP-ADCS η" },
  { key:"ETA_EP",              unit:"—",           desc:"Residual EMMS coil lag (20 ms)",            group:"EP-ADCS η" },
  { key:"W3_FRACTION",         unit:"—",           desc:"W3: oscillatory perturbation saving",       group:"Savings" },
  { key:"W6_FRACTION",         unit:"—",           desc:"W6: day-night differential drag saving",    group:"Savings" },
  { key:"W4_ANNUAL_PER_100KG", unit:"kg/yr/100kg", desc:"W4: desaturation propellant per 100 kg",   group:"Savings" },
  { key:"TIER_SKM4",           unit:"—",           desc:"SKM-4 tier multiplier (max mechanisms)",    group:"Savings" },
  { key:"TIER_SKM1",           unit:"—",           desc:"SKM-1 tier multiplier (min mechanisms)",    group:"Savings" },
  { key:"W6_MULT",             unit:"—",           desc:"W6 final combined multiplier",              group:"Savings" },
];

function computeAll(C) {
  const ETA_CONV = C.ETA_INTRACK * C.ETA_OSCILLATORY;
  const rho425 = C.RHO_A * Math.exp((C.RHO_REF_ALT - 425) / C.RHO_SCALE);
  const v425   = Math.sqrt(C.G / (C.RE + 425e3));
  const F425   = 0.5 * C.CD * 0.02 * rho425 * v425 * v425;
  const dv5yr  = (F425 * C.SEC_PER_YEAR / 8) * 5;
  const ve     = 70 * C.G0;
  const ideal  = 8 * (dv5yr / ve);
  const conv   = ideal * ETA_CONV;
  const ep     = ideal * C.ETA_EP * C.TIER_SKM4 * C.W6_MULT;
  const saving = (conv - ep) / conv;
  const mdot   = F425 / ve;
  const leverage = ((ETA_CONV - 1) * mdot * (v425 * ve) * 86400) / (0.4e-3 * 86400);
  return { ETA_CONV, rho425, v425, F425, dv5yr, ideal, conv, ep, saving, leverage };
}

function validateAll(vals, C) {
  return [
    { label:"ρ(425km) reasonable",   pass: vals.rho425 > 1e-13 && vals.rho425 < 1e-10, value: vals.rho425.toExponential(2)+" kg/m³" },
    { label:"v_orb(425km) ≈7650 m/s",pass: Math.abs(vals.v425 - 7649) < 200,           value: vals.v425.toFixed(0)+" m/s" },
    { label:"F_drag > 0 N",           pass: vals.F425 > 0,                               value: (vals.F425*1e6).toFixed(3)+" μN" },
    { label:"ETA_CONV > 1",           pass: vals.ETA_CONV > 1,                           value: vals.ETA_CONV.toFixed(3) },
    { label:"ETA_EP < ETA_CONV×0.6",  pass: C.ETA_EP < vals.ETA_CONV * 0.6,             value: C.ETA_EP.toFixed(3) },
    { label:"saving 45–65%",          pass: vals.saving > 0.45 && vals.saving < 0.65,    value: (vals.saving*100).toFixed(1)+"%" },
    { label:"ep < conv",              pass: vals.ep < vals.conv,                          value: vals.ep < vals.conv ? "✓" : "✗" },
    { label:"leverage 40–65×",        pass: vals.leverage > 40 && vals.leverage < 65,    value: vals.leverage.toFixed(1)+"×" },
  ];
}

function fmt(n) {
  if (Math.abs(n) >= 1e6 || (Math.abs(n) < 0.001 && n !== 0)) return n.toExponential(3);
  return n.toPrecision(5).replace(/\.?0+$/, "");
}

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    (acc[item[key]] = acc[item[key]] || []).push(item);
    return acc;
  }, {});
}

// ── Password modal ────────────────────────────────────────────
function EditPasswordModal({ onSuccess, onClose, password }) {
  const [input, setInput] = useState("");
  const [err, setErr] = useState(false);

  function submit(e) {
    e.preventDefault();
    if (input === password) { onSuccess(); }
    else { setErr(true); setInput(""); }
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:1000,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#080f1a", border:"1px solid #0e2035", borderRadius:10,
                    padding:"32px 40px", minWidth:320, fontFamily:"monospace" }}>
        <div style={{ color:"#e2e8f0", fontSize:15, fontWeight:700, marginBottom:8 }}>
          🔒 Unlock Editing
        </div>
        <div style={{ color:"#3a5068", fontSize:12, marginBottom:20 }}>
          Enter the admin password to enable constant editing.
        </div>
        <form onSubmit={submit}>
          <input
            autoFocus
            type="password"
            value={input}
            onChange={e => { setInput(e.target.value); setErr(false); }}
            placeholder="password"
            style={{ width:"100%", background:"#030810", border:`1px solid ${err ? "#ef4444" : "#0e2035"}`,
                     color:"#e2e8f0", padding:"8px 12px", borderRadius:5, fontSize:14,
                     fontFamily:"monospace", boxSizing:"border-box", marginBottom: err ? 6 : 16 }}
          />
          {err && <div style={{ color:"#ef4444", fontSize:12, marginBottom:12 }}>Incorrect password</div>}
          <div style={{ display:"flex", gap:10 }}>
            <button type="submit"
              style={{ flex:1, padding:"8px", background:"#1e3a5f", color:"#e2e8f0",
                       border:"1px solid #0e2035", borderRadius:5, cursor:"pointer",
                       fontFamily:"monospace", fontSize:13 }}>
              Unlock
            </button>
            <button type="button" onClick={onClose}
              style={{ padding:"8px 16px", background:"transparent", color:"#3a5068",
                       border:"1px solid #0e2035", borderRadius:5, cursor:"pointer",
                       fontFamily:"monospace", fontSize:13 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function ConstantsPage() {
  const { password, ...col } = useContext(ThemeContext);

  const [vals, setVals]         = useState({ ...DEFAULTS });
  const [edits, setEdits]       = useState({});
  const [dirty, setDirty]       = useState({});
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [showModal, setShowModal]       = useState(false);
  const [pendingKey, setPendingKey]     = useState(null);

  const computed = computeAll(vals);
  const checks   = validateAll(computed, vals);
  const allPass  = checks.every(c => c.pass);
  const grouped  = groupBy(META, "group");
  const hasDirty = Object.values(dirty).some(Boolean);

  function handleFocus(key) {
    if (!editUnlocked) {
      setPendingKey(key);
      setShowModal(true);
    }
  }

  function handleChange(key, raw) {
    if (!editUnlocked) return;
    setEdits(e => ({ ...e, [key]: raw }));
    const n = parseFloat(raw);
    if (!isNaN(n) && n !== 0) {
      setVals(v => ({ ...v, [key]: n }));
      setDirty(d => ({ ...d, [key]: DEFAULTS[key] !== n }));
    }
  }

  function handleUnlockSuccess() {
    setEditUnlocked(true);
    setShowModal(false);
    setPendingKey(null);
  }

  function reset() {
    setVals({ ...DEFAULTS });
    setEdits({});
    setDirty({});
  }

  const s = {
    wrap:  { color: col.text, fontFamily: "monospace" },
    h2:    { color: col.bright, fontSize:18, fontWeight:700, margin:"24px 0 8px" },
    table: { width:"100%", borderCollapse:"collapse", fontSize:13, marginBottom:16 },
    th:    { textAlign:"left", padding:"6px 10px", color:col.muted, fontWeight:600,
             borderBottom:`1px solid ${col.border}` },
    td:    { padding:"5px 10px", borderBottom:`1px solid ${col.border}20` },
    input: (dirty, unlocked) => ({
      background: unlocked ? "#0a1526" : "#050b14",
      border:`1px solid ${dirty ? "#f59e0b" : col.border}`,
      color: unlocked ? col.bright : col.muted,
      padding:"2px 6px", borderRadius:3, fontFamily:"monospace",
      width:130, fontSize:13,
      cursor: unlocked ? "text" : "pointer",
    }),
    badge: (pass) => ({
      display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:12,
      background: pass ? "#052e16" : "#3b0000",
      color:      pass ? "#4ade80" : "#f87171",
      border:     `1px solid ${pass ? "#166534" : "#7f1d1d"}`,
    }),
    panel: { background:col.panel, border:`1px solid ${col.border}`, borderRadius:8,
             padding:"12px 16px", marginBottom:16 },
    row:   { display:"flex", justifyContent:"space-between", alignItems:"center",
             padding:"3px 0", borderBottom:`1px solid ${col.border}20` },
    label: { color:col.muted, fontSize:13, flex:1 },
    val:   { color:col.bright, fontFamily:"monospace", fontSize:13 },
    btn:   { background:"#1e3a5f", color:col.bright, border:`1px solid ${col.border}`,
             padding:"6px 16px", borderRadius:6, cursor:"pointer",
             fontFamily:"monospace", fontSize:13, marginRight:8 },
    statusBar: {
      padding:"8px 14px", borderRadius:6, marginBottom:16, fontSize:13,
      background: allPass ? "#052e16" : "#3b0000",
      color:      allPass ? "#4ade80" : "#f87171",
      border:     `1px solid ${allPass ? "#166534" : "#7f1d1d"}`,
    },
    lockBar: {
      padding:"7px 14px", borderRadius:6, marginBottom:12, fontSize:12,
      background: editUnlocked ? "#0c2e1a" : "#1a1a2e",
      color:      editUnlocked ? "#4ade80" : "#94a3b8",
      border:     `1px solid ${editUnlocked ? "#166534" : "#1e2d45"}`,
      display:"flex", alignItems:"center", justifyContent:"space-between",
    },
  };

  return (
    <div style={s.wrap}>
      {showModal && (
        <EditPasswordModal
          password={password}
          onSuccess={handleUnlockSuccess}
          onClose={() => setShowModal(false)}
        />
      )}

      <div style={{ color:col.muted, fontSize:13, marginBottom:12 }}>
        View all physics constants and their live effect on computed values.
        Source of truth:{" "}
        <code style={{ color:"#5bc4e3" }}>src/physics/constants.js</code>
      </div>

      <div style={s.lockBar}>
        <span>{editUnlocked ? "✓ Edit mode unlocked" : "🔒 Read-only — click any field to unlock editing"}</span>
        {editUnlocked && (
          <button style={{ ...s.btn, padding:"3px 10px", fontSize:11 }}
            onClick={() => setEditUnlocked(false)}>Lock</button>
        )}
      </div>

      <div style={s.statusBar}>
        {allPass ? "✓ All validation checks pass" : "✗ Some checks failing — see Validation panel"}
        {hasDirty && " · Modified constants highlighted in orange"}
      </div>

      {hasDirty && (
        <div style={{ marginBottom:12 }}>
          <button style={s.btn} onClick={reset}>⟳ Reset all to defaults</button>
        </div>
      )}

      {Object.entries(grouped).map(([group, rows]) => (
        <div key={group}>
          <div style={s.h2}>{group}</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Constant</th>
                <th style={s.th}>Value</th>
                <th style={s.th}>Unit</th>
                <th style={s.th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, unit, desc }) => {
                const isDirty = dirty[key];
                return (
                  <tr key={key}>
                    <td style={{ ...s.td, color: isDirty ? "#f59e0b" : "#5bc4e3", fontWeight:600 }}>
                      {key}
                    </td>
                    <td style={s.td}>
                      <input
                        style={s.input(isDirty, editUnlocked)}
                        value={edits[key] !== undefined ? edits[key] : fmt(vals[key])}
                        readOnly={!editUnlocked}
                        onFocus={() => handleFocus(key)}
                        onChange={e => handleChange(key, e.target.value)}
                        onBlur={() => setEdits(e => { const n = {...e}; delete n[key]; return n; })}
                        title={editUnlocked ? "Edit value" : "Click to unlock editing"}
                      />
                    </td>
                    <td style={{ ...s.td, color:col.muted }}>{unit}</td>
                    <td style={{ ...s.td, color:col.text }}>{desc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <div style={s.h2}>Live Computed Values</div>
      <div style={s.panel}>
        {[
          ["η_conv = η_intrack × η_osc",       computed.ETA_CONV.toFixed(4)],
          ["ρ(425 km)",                         computed.rho425.toExponential(3)+" kg/m³"],
          ["v_orb(425 km)",                     computed.v425.toFixed(1)+" m/s"],
          ["F_drag(425km, 0.02m²)",             (computed.F425*1e6).toFixed(3)+" μN"],
          ["ΔV/yr (8kg, 0.02m², 425km)",        (computed.dv5yr/5).toFixed(4)+" m/s/yr"],
          ["ΔV × 5 yr",                         computed.dv5yr.toFixed(4)+" m/s"],
          ["Ideal propellant (Isp=70s, 5yr)",   computed.ideal.toFixed(4)+" kg"],
          ["Conventional propellant",            computed.conv.toFixed(4)+" kg"],
          ["EP-ADCS propellant (SKM4)",          computed.ep.toFixed(4)+" kg"],
          ["Saving fraction (SKM4)",             (computed.saving*100).toFixed(1)+"%"],
          ["Energy leverage (0.4mW EMMS)",       computed.leverage.toFixed(1)+"×"],
        ].map(([label, val]) => (
          <div key={label} style={s.row}>
            <span style={s.label}>{label}</span>
            <span style={s.val}>{val}</span>
          </div>
        ))}
      </div>

      <div style={s.h2}>Validation Checks</div>
      <div style={s.panel}>
        {checks.map(({ label, pass, value }) => (
          <div key={label} style={{ ...s.row, gap:8 }}>
            <span style={{ ...s.label }}>{label}</span>
            <span style={{ ...s.val, flex:1, textAlign:"center" }}>{value}</span>
            <span style={s.badge(pass)}>{pass ? "PASS" : "FAIL"}</span>
          </div>
        ))}
      </div>

      <div style={{ color:col.muted, fontSize:12, marginTop:8 }}>
        To persist changes: update <code style={{ color:"#5bc4e3" }}>src/physics/constants.js</code>
        {" · "}Validate with: <code style={{ color:"#5bc4e3" }}>npm test -- --watchAll=false</code>
      </div>
    </div>
  );
}
